// frontend/user/src/_lib/transfer-persist.ts
//
// 传输列表持久化:IndexedDB(idb)落库 + 纯序列化映射。
// File 无法序列化 → toPersistable 剥离;刷新后 uploading/pending revive 为 interrupted。
// IDB 调用全部 lazy(只在函数体内 openDB),模块顶层不触 indexedDB,以便 jsdom 单测纯函数。
// 设计来源:docs/superpowers/plans/2026-06-07-transfers-persist-and-speed.md 任务 2。

import { openDB, type IDBPDatabase } from 'idb'

export type UploadTaskStatus = 'pending' | 'uploading' | 'paused' | 'done' | 'error' | 'interrupted'

// 上传子阶段(仅 status==='uploading' 有意义):preparing=算 hash 期、transferring=传字节期、
// finalizing=大文件传完等后端 postprocess 期。瞬时态,不持久化(同 speedBps)。
export type UploadPhase = 'preparing' | 'transferring' | 'finalizing'

// 与 upload-store.UploadTask 同构的最小视图(避免循环依赖,store 复用本类型扩展)。
export type AnyTask = {
  id: string
  name: string
  size: number
  bytesUploaded: number
  percent: number
  status: UploadTaskStatus
  // error/speedBps/file 可被显式赋 undefined 清除(exactOptionalPropertyTypes 下须带 | undefined)。
  error?: string | undefined
  startedAt: number
  speedBps?: number | undefined
  file?: File | undefined
  // 瞬时上传子阶段;不持久化(刷新后任务必转 interrupted,phase 无意义)。
  phase?: UploadPhase | undefined
  // 瞬时 hash 进度(0–100,仅 phase==='preparing' 有意义);不持久化(同 phase/speedBps)。
  hashPercent?: number | undefined
  // 首次上传算出的整文件 sha256;暂停/恢复同会话内复用,避免 resume 重算整个文件 hash
  // (大文件重算会卡顿且延迟 /big/init)。不持久化:刷新后 File 已丢、任务转 interrupted,
  // sha 无意义(重选文件时会重新现算)。
  sha?: string | undefined
}

// 持久化对象:去掉 file(不可序列化)、speedBps、phase、hashPercent 与 sha(瞬时态)。
export type PersistedTask = Omit<AnyTask, 'file' | 'speedBps' | 'phase' | 'hashPercent' | 'sha'>

// 显式构造(避免 destructure-omit 触发 no-unused-vars)。
export function toPersistable(t: AnyTask): PersistedTask {
  return {
    id: t.id,
    name: t.name,
    size: t.size,
    bytesUploaded: t.bytesUploaded,
    percent: t.percent,
    status: t.status,
    startedAt: t.startedAt,
    ...(t.error !== undefined ? { error: t.error } : {}),
  }
}

// reviveTask 启动 rehydrate:uploading/pending/paused(File 已丢、无法自动续)→ interrupted;速度清零。
// 暂停态(paused)同理:刷新后内存 File 丢失,无法点恢复直接续 → 降级为「已中断」走重选续传。
export function reviveTask(p: PersistedTask): AnyTask {
  const status: UploadTaskStatus =
    p.status === 'uploading' || p.status === 'pending' || p.status === 'paused'
      ? 'interrupted'
      : p.status
  return { ...p, status, speedBps: undefined, file: undefined }
}

const DB_NAME = 'dogdisk-transfers'
const STORE = 'tasks'
const DB_VERSION = 1

let dbPromise: Promise<IDBPDatabase> | null = null
function db(): Promise<IDBPDatabase> {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(d) {
        if (!d.objectStoreNames.contains(STORE)) {
          d.createObjectStore(STORE, { keyPath: 'id' })
        }
      },
    })
  }
  return dbPromise
}

export function persistAvailable(): boolean {
  return typeof indexedDB !== 'undefined'
}

export async function loadPersistedTasks(): Promise<AnyTask[]> {
  if (!persistAvailable()) return []
  try {
    const all = (await (await db()).getAll(STORE)) as PersistedTask[]
    return all.sort((a, b) => a.startedAt - b.startedAt).map(reviveTask)
  } catch {
    return []
  }
}

export async function putPersistedTask(t: AnyTask): Promise<void> {
  if (!persistAvailable()) return
  try {
    await (await db()).put(STORE, toPersistable(t))
  } catch {
    // best-effort:持久化失败不阻断上传
  }
}

export async function deletePersistedTask(id: string): Promise<void> {
  if (!persistAvailable()) return
  try {
    await (await db()).delete(STORE, id)
  } catch {
    // best-effort
  }
}

export async function deletePersistedWhere(pred: (t: PersistedTask) => boolean): Promise<void> {
  if (!persistAvailable()) return
  try {
    const d = await db()
    const all = (await d.getAll(STORE)) as PersistedTask[]
    const tx = d.transaction(STORE, 'readwrite')
    await Promise.all(all.filter(pred).map((t) => tx.store.delete(t.id)))
    await tx.done
  } catch {
    // best-effort
  }
}
